import React, { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { bibleBooks } from "./data/bibleBooks";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// Helper to format date as 'Month day, year'
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function SermonSearch() {
  const [book, setBook] = useState("");
  const [chapter, setChapter] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedSermon, setSelectedSermon] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleBookChange = (event, newValue) => {
    setBook(newValue || "");
    setChapter("");
  };

  const handleChapterChange = (e) => {
    setChapter(e.target.value);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResults([]);
    setHasSearched(true);
    try {
      let query = "";
      if (book && chapter) {
        query = `?chapter=${encodeURIComponent(`${book} ${chapter}`)}`;
      } else if (book) {
        query = `?book=${encodeURIComponent(book)}`;
      }
      const res = await fetch(`/api/SermonSearch${query}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      // Sort by chapter (numerically if possible), then by date (descending)
      const sorted = [...data].sort((a, b) => {
        // Extract chapter number from Text (assumes format 'Book Chapter:')
        const getChapter = (sermon) => {
          const match = sermon.Text && sermon.Text.match(/\b(\d+):/);
          return match ? parseInt(match[1], 10) : 0;
        };
        const chapA = getChapter(a);
        const chapB = getChapter(b);
        if (chapA !== chapB) return chapA - chapB;
        // If chapters are equal, sort by date descending
        const dateA = a.Date ? new Date(a.Date) : new Date(0);
        const dateB = b.Date ? new Date(b.Date) : new Date(0);
        return dateB - dateA;
      });
      setResults(sorted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const chapters = book
    ? bibleBooks.find((b) => b.name === book)?.chapters
      ? Array.from(
          { length: bibleBooks.find((b) => b.name === book).chapters },
          (_, i) => (i + 1).toString(),
        )
      : []
    : [];

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom align="center">
          Search Sermons
        </Typography>
        <>
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Autocomplete
              value={book}
              onChange={handleBookChange}
              options={bibleBooks.map((b) => b.name)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Book"
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 180 }}
                />
              )}
              disableClearable
              autoHighlight
              autoSelect
              sx={{ minWidth: 180 }}
            />
            <TextField
              select
              label="Chapter"
              value={chapter}
              onChange={handleChapterChange}
              disabled={!book}
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">Select Chapter</MenuItem>
              {chapters.map((ch) => (
                <MenuItem key={ch} value={ch}>
                  {ch}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !book}
              sx={{ minWidth: 100, height: 40 }}
            >
              {loading ? <CircularProgress size={22} /> : "Search"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box>
            {results.length > 0 && (
              <Typography variant="h6" sx={{ mb: 2 }}>
                Results
              </Typography>
            )}
            <Box component="ul" sx={{ pl: 2 }}>
              {hasSearched && results.length === 0 && !loading && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  No sermons found for your search.
                </Typography>
              )}
              {results.map((sermon, i) => (
                <Paper
                  key={sermon.id || i}
                  elevation={1}
                  sx={{
                    mb: 3,
                    p: 2,
                    background: "#f8f9fa",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: 6 },
                  }}
                  component="li"
                  onClick={() => {
                    setSelectedSermon(sermon);
                    setOpenModal(true);
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {sermon.Text}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {sermon.Title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sermon.Date ? `Date: ${formatDate(sermon.Date)}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sermon.Speaker ? `Speaker: ${sermon.Speaker}` : ""}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
          <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {selectedSermon?.Text}
              <IconButton
                onClick={() => setOpenModal(false)}
                size="small"
                sx={{ ml: 2 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ minWidth: 600 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {selectedSermon?.Title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {selectedSermon?.Date
                  ? `Date: ${formatDate(selectedSermon.Date)}`
                  : ""}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {selectedSermon?.Speaker
                  ? `Speaker: ${selectedSermon.Speaker}`
                  : ""}
              </Typography>
              {selectedSermon?.Series && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Series: {selectedSermon.Series}
                </Typography>
              )}
              <Typography
                variant="h6"
                color="primary"
                sx={{ mt: 3, mb: 0.5, fontWeight: 700, letterSpacing: 1 }}
              >
                Summary
              </Typography>
              <Box
                sx={{
                  borderBottom: 2,
                  borderColor: "primary.main",
                  width: 60,
                  mb: 1,
                }}
              />
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedSermon?.Summary}
              </Typography>
              {selectedSermon?.KeyPoints &&
                selectedSermon.KeyPoints.length > 0 && (
                  <>
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ mt: 3, mb: 0.5, fontWeight: 700, letterSpacing: 1 }}
                    >
                      Key Points
                    </Typography>
                    <Box
                      sx={{
                        borderBottom: 2,
                        borderColor: "primary.main",
                        width: 100,
                        mb: 1,
                      }}
                    />
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                      {selectedSermon.KeyPoints.map((pt, j) => (
                        <li key={j}>{pt}</li>
                      ))}
                    </Box>
                  </>
                )}
              {selectedSermon?.Transcript && (
                <details>
                  <summary>Transcript</summary>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                  >
                    {selectedSermon.Transcript}
                  </Typography>
                </details>
              )}
            </DialogContent>
          </Dialog>
        </>
      </Paper>
    </Box>
  );
}

export default SermonSearch;
